import type { PerformancePart } from '@tunasong/schemas'
import { partsLabel, partsList } from '@tunasong/schemas'
import { Button, Snackbar } from '@tunasong/ui-lib'
import { useEffect, useRef, useState, type FC } from 'react'

export interface DefaultPartNoticeProps {
  parts: PerformancePart[]
  /** "Change for this band": choose the parts for this band explicitly. */
  onChangeForBand: () => void
  /** Called once, when the notice is first shown, so it is not shown for this band again. */
  onShown: () => void
  /** Positions the notice, e.g. above a view's fixed bottom bars on phones. */
  className?: string
}

/** How long the notice stays before it goes away on its own. */
const noticeDuration = 8000

/**
 * One-time, non-blocking notice when a band song uses the profile default: "Playing Guitar, your
 * default part." It is a polite status, never an alert, and takes no focus from the song.
 */
export const DefaultPartNotice: FC<DefaultPartNoticeProps> = ({ parts, onChangeForBand, onShown, className }) => {
  const [open, setOpen] = useState(true)
  const reported = useRef(false)

  useEffect(() => {
    if (reported.current) {
      return
    }
    reported.current = true
    onShown()
  }, [onShown])

  /** "Vocals + Guitar" for one or two parts; the notice names every part when there are more. */
  const names = parts.length > 2 ? partsList(parts) : partsLabel(parts)
  const message = parts.length > 1 ? `Playing ${names}, your default parts.` : `Playing ${names}, your default part.`

  const handleChange = () => {
    setOpen(false)
    onChangeForBand()
  }

  return (
    <Snackbar
      className={className}
      open={open}
      onClose={() => setOpen(false)}
      autoHideDuration={noticeDuration}
      message={message}
      action={
        <Button variant="text" size="small" color="inherit" onClick={handleChange}>
          Change for this band
        </Button>
      }
    />
  )
}

export default DefaultPartNotice
